import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Mail, Phone, MessageSquare } from "lucide-react";

const STATUSES = ["new", "read", "replied", "archived"];

export default function AdminContactMessages() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/contact-messages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contact-messages/${id}`, { status });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this message?")) return;
    await api.delete(`/admin/contact-messages/${id}`);
    load();
  };

  const filtered = filter === "all" ? items : items.filter((m) => (m.status || "new") === filter);

  return (
    <div data-testid="admin-contact-messages">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Inbox · Contact Form</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-8">Contact Messages.</h1>

      <div className="flex gap-2 mb-6">
        <FilterBtn v="all" cur={filter} set={setFilter}>All ({items.length})</FilterBtn>
        {STATUSES.map((s) => (
          <FilterBtn key={s} v={s} cur={filter} set={setFilter}>
            {s.toUpperCase()} ({items.filter((m) => (m.status || "new") === s).length})
          </FilterBtn>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((m) => (
          <div key={m.id} data-testid={`msg-${m.id}`} className="border border-white/12 bg-[#131A2B] p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <MessageSquare size={16} className="brand-blue" />
                  <span className="font-bold">{m.name}</span>
                  <span className="text-xs font-mono text-white/50">{m.received_at?.slice(0, 16).replace("T", " ")}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${
                    m.status === "new" ? "bg-brand-blue/20 text-brand-blue" :
                    m.status === "replied" ? "bg-brand-orange/20 text-brand-orange" :
                    m.status === "archived" ? "bg-white/10 text-white/40" :
                    "bg-white/10 text-white/60"
                  }`}>{m.status || "new"}</span>
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs font-mono text-white/60">
                  <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:brand-blue"><Mail size={12} /> {m.email}</a>
                  {m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-1 hover:brand-blue"><Phone size={12} /> {m.phone}</a>}
                </div>
                {m.subject && <div className="mt-3 font-display text-lg">{m.subject}</div>}
                <p className="mt-2 text-sm text-white/80 whitespace-pre-wrap">{m.message}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <select value={m.status || "new"} onChange={(e) => setStatus(m.id, e.target.value)} data-testid={`msg-status-${m.id}`} className="bg-transparent border border-white/20 px-2 py-1 text-xs">
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-[#131A2B]">{s}</option>)}
                </select>
                <button onClick={() => del(m.id)} data-testid={`msg-del-${m.id}`} className="p-2 text-white/60 hover:text-red-400 border border-white/20"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center py-16 text-white/40">No messages in this filter.</div>}
      </div>
    </div>
  );
}

function FilterBtn({ v, cur, set, children }) {
  return (
    <button
      onClick={() => set(v)}
      data-testid={`msg-filter-${v}`}
      className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border ${cur === v ? "bg-brand-blue text-white border-brand-blue" : "border-white/20 text-white/60 hover:border-white/50"}`}
    >
      {children}
    </button>
  );
}
